import * as React from 'react';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import Headline from '@adecco/base-app/src/components/atoms/Headline/Headline';
import Paragraph from '@adecco/base-app/src/components/atoms/Paragraph/Paragraph';
import { moreThan, lessThan } from '@adecco/base-app/src/constants/breakpoints/breakpoints';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useAppContext } from '@adecco/base-app/src/utilities/appProvider/appProvider';
import Icon from '@adecco/base-app/src/components/atoms/Icon/Icon';

const AnimateHeight = dynamic(() => import('react-animate-height'), { ssr: false });

export interface IFaqItem {
  title: string;
  text: string;
  slug?: string;
}
export interface IFaqItemStyled {
  open?: boolean;
}

const FaqItemStyled = styled.div<IFaqItemStyled>`
  padding: 20px 25px;
  border: ${(props) => props.theme.borders.materialDown};
  box-shadow: ${(props) =>
    props.open ? props.theme.boxShadow.faqItemOpen : props.theme.boxShadow.faqItem};
  border-radius: ${(props) => props.theme.radius.faqItem};
  cursor: pointer;
  background-color: ${(props) => props.theme.backgrounds.faqItem};
  min-height: ${(props) => props.theme.heights.faqItem};

  ${moreThan('md')} {
    padding: ${(props) =>
      props.open ? props.theme.paddings.faqItemOpen : props.theme.paddings.faqItemClose};
    transition: padding-bottom 0.5s ease-in-out;
  }
`;

const FAQItemHeadline = styled(Headline)<IFaqItemStyled>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  position: relative;
  padding: 0 35px 0 0;
  transition: padding 0.3s ease-in-out;
  font-family: ${(props) => props.theme.fonts.HeadlineBold};
  font-size: ${(props) => props.theme.fontSizes.faqItemHeadline};
  margin-bottom: ${(props) => (props.open ? '12px' : '0')};
  transition: margin-bottom 0.5s ease-in-out;
  line-height: ${(props) => props.theme.lineHeights.headline3};

  ${moreThan('md')} {
    font-family: ${(props) => props.theme.fonts.Headline};
    font-size: ${(props) => props.theme.fontSizes.faqItemHeadlineWideScreen};
    padding: 0;
    margin-bottom: ${(props) => (props.open ? '14px' : '0')};
    margin-top: ${(props) => props.theme.margins.faqItemHeadlineWideScreen};
  }

  &::before,
  &::after {
    display: ${(props) => props.theme.displays.faqLeftOpenButton};
  }

  &::before {
    content: '';
    position: absolute;
    top: 12px;
    left: ${(props) => (props.open ? '-47px' : '-42px')};
    ${lessThan('md')} {
      left: ${(props) => (props.open ? 'calc(100% - 15px)' : 'calc(100% - 18px)')};
      width: ${(props) => (props.open ? '15px' : '18px')};
    }
    width: ${(props) => (props.open ? '15px' : '20px')};
    height: ${(props) => props.theme.heights.faqItemOpenElement};
    background-color: ${(props) => props.theme.colors.faqItemOpenElement};
    transform: ${(props) => (props.open ? 'rotate(-45deg)' : 'rotate(0)')};
    ${lessThan('md')} {
      transform: ${(props) => (props.open ? 'rotate(45deg)' : 'rotate(0)')};
    }
    transition: transform 0.2s ease-in-out, left 0.2s ease-in-out, top 0.2s ease-in-out;
  }
  &::after {
    content: '';
    position: absolute;
    top: 12px;
    left: ${(props) => (props.open ? '-37px' : '-42px')};
    ${lessThan('md')} {
      left: ${(props) => (props.open ? 'calc(100% - 25px)' : 'calc(100% - 18px)')};
      width: ${(props) => (props.open ? '15px' : '18px')};
    }
    width: ${(props) => (props.open ? '15px' : '20px')};
    height: ${(props) => props.theme.heights.faqItemOpenElement};
    background-color: ${(props) => props.theme.colors.faqItemOpenElement};
    transform: ${(props) => (props.open ? 'rotate(45deg)' : 'rotate(90deg)')};
    ${lessThan('md')} {
      transform: ${(props) => (props.open ? 'rotate(135deg)' : 'rotate(90deg)')};
    }
    transition: transform 0.2s ease-in-out, left 0.2s ease-in-out, top 0.2s ease-in-out;
  }
`;

const FAQItemParagraph = styled(Paragraph)`
  line-height: ${(props) => props.theme.lineHeights.paragraph};
  font-size: ${(props) => props.theme.fontSizes.faqItemParagraph};

  ${moreThan('md')} {
    line-height: ${(props) => props.theme.lineHeights.paragraphWideScreen};
  }
`;

const ProServButton = styled.div<IFaqItemStyled>`
  position: absolute;
  top: -12px;
  right: 0px;
  background-color: #ffd602;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  padding: 4px;
  flex-grow: 42px;
  transform: ${(props) => (props.open ? 'rotate(45deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease-in-out, left 0.2s ease-in-out, top 0.2s ease-in-out;
`;

const FaqItem: React.FunctionComponent<IFaqItem> = ({ title, text, slug }) => {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { brand } = useAppContext();

  useEffect(() => {
    if (!slug) return;
    if (router.asPath.split('#')[1] === slug) setOpen(true);
  }, [router.asPath, slug]);

  return (
    <FaqItemStyled onClick={() => setOpen(!open)} open={open}>
      {slug && <span id={slug} />}
      <FAQItemHeadline skin={3} tag="h4" open={open}>
        {title}
        {brand === 'ProServ' && (
          <ProServButton open={open}>
            <Icon name="plus" fill="black" />
          </ProServButton>
        )}
      </FAQItemHeadline>
      <AnimateHeight
        duration={500}
        easing="ease-in-out"
        height={open ? 'auto' : 0} // see props documentation below
      >
        <FAQItemParagraph>{text}</FAQItemParagraph>
      </AnimateHeight>
    </FaqItemStyled>
  );
};

export default FaqItem;
